/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { getApiUrl } from "@/utils/baseUrl";

export interface DockerHost {
  id: number;
  name: string;
  host: string; // e.g. "unix:///var/run/docker.sock"
  pollIntervalSeconds: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DockerContainerStats {
  id: number;
  containerRowId: number;
  cpuPercent: number;
  memUsageBytes: number;
  memLimitBytes: number;
  netRxBytes: number;
  netTxBytes: number;
  blockReadBytes: number;
  blockWriteBytes: number;
  createdAt: string;
}

export interface DockerContainer {
  id: number;
  hostId: number;
  containerId: string;
  name: string;
  image: string;
  state: string; // running, exited, paused, restarting, dead, ...
  status: string;
  composeProject?: string;
  composeService?: string;
  containerCreatedAt: string;
  updatedAt: string;
  latest?: DockerContainerStats;
}

export interface PaginatedDockerContainerStats {
  data: DockerContainerStats[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateDockerHostRequest {
  name: string;
  host: string;
  pollIntervalSeconds?: number;
  enabled: boolean;
}

export interface UpdateDockerHostRequest extends CreateDockerHostRequest {
  id: number;
}

async function parseErrorOr(response: Response, fallback: string): Promise<never> {
  const errorData = await response.json().catch(() => ({}));
  throw new Error(errorData.error || fallback);
}

// Host management
export async function getDockerHosts(): Promise<DockerHost[]> {
  const response = await fetch(getApiUrl("/docker/hosts"));
  if (!response.ok) return parseErrorOr(response, "Failed to fetch hosts");
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

export async function getDockerHost(id: number): Promise<DockerHost> {
  const response = await fetch(getApiUrl(`/docker/hosts/${id}`));
  if (!response.ok) return parseErrorOr(response, "Failed to fetch host");
  return response.json();
}

export async function createDockerHost(data: CreateDockerHostRequest): Promise<DockerHost> {
  const response = await fetch(getApiUrl("/docker/hosts"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) return parseErrorOr(response, "Failed to create host");
  return response.json();
}

export async function updateDockerHost(
  id: number,
  data: UpdateDockerHostRequest,
): Promise<DockerHost> {
  const response = await fetch(getApiUrl(`/docker/hosts/${id}`), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) return parseErrorOr(response, "Failed to update host");
  return response.json();
}

export async function deleteDockerHost(id: number): Promise<void> {
  const response = await fetch(getApiUrl(`/docker/hosts/${id}`), { method: "DELETE" });
  if (!response.ok) return parseErrorOr(response, "Failed to delete host");
}

// Containers
export async function getDockerHostContainers(hostId: number): Promise<DockerContainer[]> {
  const response = await fetch(getApiUrl(`/docker/hosts/${hostId}/containers`));
  if (!response.ok) return parseErrorOr(response, "Failed to fetch containers");
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

export async function getDockerContainerStats(
  hostId: number,
  containerId: string,
  page = 1,
  limit = 240,
): Promise<PaginatedDockerContainerStats> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  const response = await fetch(
    getApiUrl(`/docker/hosts/${hostId}/containers/${containerId}/stats?${params}`),
  );
  if (!response.ok) return parseErrorOr(response, "Failed to fetch container stats");
  return response.json();
}

export async function startDockerContainer(hostId: number, containerId: string): Promise<void> {
  const response = await fetch(
    getApiUrl(`/docker/hosts/${hostId}/containers/${containerId}/start`),
    { method: "POST" },
  );
  if (!response.ok) return parseErrorOr(response, "Failed to start container");
}

export async function stopDockerContainer(hostId: number, containerId: string): Promise<void> {
  const response = await fetch(
    getApiUrl(`/docker/hosts/${hostId}/containers/${containerId}/stop`),
    { method: "POST" },
  );
  if (!response.ok) return parseErrorOr(response, "Failed to stop container");
}

export async function restartDockerContainer(hostId: number, containerId: string): Promise<void> {
  const response = await fetch(
    getApiUrl(`/docker/hosts/${hostId}/containers/${containerId}/restart`),
    { method: "POST" },
  );
  if (!response.ok) return parseErrorOr(response, "Failed to restart container");
}

export async function getDockerContainerLogs(
  hostId: number,
  containerId: string,
  tail = 200,
): Promise<string> {
  const params = new URLSearchParams({ tail: String(tail) });
  const response = await fetch(
    getApiUrl(`/docker/hosts/${hostId}/containers/${containerId}/logs?${params}`),
  );
  if (!response.ok) return parseErrorOr(response, "Failed to fetch container logs");
  const data = await response.json();
  return data.logs ?? "";
}

// ---- Portainer-style management API ----

export interface DockerInfo {
  name: string;
  serverVersion: string;
  os: string;
  kernel: string;
  driver: string;
  cpus: number;
  memTotal: number;
  containers: number;
  running: number;
  paused: number;
  stopped: number;
  images: number;
}

export interface DockerPort {
  ip?: string;
  private: number;
  public?: number;
  type: string;
}

export interface DockerLiveContainer {
  id: string;
  name: string;
  image: string;
  state: string;
  status: string;
  command: string;
  created: number;
  project?: string;
  service?: string;
  ports: DockerPort[];
  networks: { network: string; ip: string }[];
  mounts: number;
}

export interface DockerImage {
  id: string;
  tags: string[];
  size: number;
  created: number;
  containers: number;
}

export interface DockerVolume {
  name: string;
  driver: string;
  scope: string;
  mountpoint: string;
  created?: string;
  size: number;
  refCount: number;
}

export interface DockerNetwork {
  id: string;
  name: string;
  driver: string;
  scope: string;
  internal: boolean;
  created: string;
  subnets: string[];
  gateways: string[];
}

export interface DockerInspect {
  id: string;
  name: string;
  image: string;
  imageId: string;
  created: string;
  platform: string;
  driver: string;
  status: string;
  running: boolean;
  paused: boolean;
  restarting: boolean;
  oomKilled: boolean;
  exitCode: number;
  error?: string;
  startedAt: string;
  finishedAt: string;
  health?: string;
  restartCount: number;
  hostname: string;
  user?: string;
  workingDir?: string;
  entrypoint: string[];
  cmd: string[];
  env: string[];
  labels: Record<string, string>;
  restartPolicy: string;
  networkMode: string;
  privileged: boolean;
  memoryLimit: number;
  nanoCpus: number;
  ports: DockerPort[];
  mounts: { type: string; name?: string; source: string; destination: string; rw: boolean }[];
  networks: { network: string; ip: string; gateway: string; mac: string; aliases: string[] }[];
}

async function getJSON<T>(path: string, what: string): Promise<T> {
  const response = await fetch(getApiUrl(path));
  if (!response.ok) return parseErrorOr(response, `Failed to fetch ${what}`);
  return (await response.json()) as T;
}

export const getDockerInfo = (hostId: number) => getJSON<DockerInfo>(`/docker/hosts/${hostId}/info`, "host info");

export async function getDockerLiveContainers(hostId: number): Promise<DockerLiveContainer[]> {
  const list = await getJSON<DockerLiveContainer[] | null>(`/docker/hosts/${hostId}/live`, "containers");
  return (list ?? []).map((c) => ({ ...c, ports: c.ports ?? [], networks: c.networks ?? [] }));
}

export async function getDockerImages(hostId: number): Promise<DockerImage[]> {
  return (await getJSON<DockerImage[] | null>(`/docker/hosts/${hostId}/images`, "images")) ?? [];
}

export async function getDockerVolumes(hostId: number): Promise<DockerVolume[]> {
  return (await getJSON<DockerVolume[] | null>(`/docker/hosts/${hostId}/volumes`, "volumes")) ?? [];
}

export async function getDockerNetworks(hostId: number): Promise<DockerNetwork[]> {
  return (await getJSON<DockerNetwork[] | null>(`/docker/hosts/${hostId}/networks`, "networks")) ?? [];
}

export const inspectDockerContainer = (hostId: number, containerId: string) =>
  getJSON<DockerInspect>(`/docker/hosts/${hostId}/containers/${containerId}/inspect`, "container");

export type DockerContainerAction = "start" | "stop" | "restart" | "pause" | "unpause" | "kill" | "remove";

/** One lifecycle action on one container. `remove` deletes it (never forced). */
export async function runDockerContainerAction(
  hostId: number,
  containerId: string,
  action: DockerContainerAction,
): Promise<void> {
  const base = `/docker/hosts/${hostId}/containers/${containerId}`;
  const response = await fetch(getApiUrl(action === "remove" ? base : `${base}/${action}`), {
    method: action === "remove" ? "DELETE" : "POST",
  });
  if (!response.ok) return parseErrorOr(response, `Failed to ${action} container`);
}

export interface DockerUpdateInfo {
  containerId: string;
  image: string;
  available: boolean;
  checked: boolean;
}

/** Which containers have a newer image in their registry (asks the registry, so it can be slow). */
export async function getDockerUpdates(hostId: number): Promise<DockerUpdateInfo[]> {
  return (await getJSON<DockerUpdateInfo[] | null>(`/docker/hosts/${hostId}/updates`, "image updates")) ?? [];
}

/** Pull the newest image and recreate the container from it. */
export async function updateDockerContainer(hostId: number, containerId: string): Promise<void> {
  const response = await fetch(getApiUrl(`/docker/hosts/${hostId}/containers/${containerId}/update`), { method: "POST" });
  if (!response.ok) return parseErrorOr(response, "Failed to update container");
}
